import { PieChart } from '@loop/ui'
import { Box, Typography } from '@mui/material'
import get from 'lodash/get'
import { useCallback, useMemo, useState } from 'react'
import { Sector } from 'recharts'
import { DelIntelThemeGroupby } from 'src/services/openApiV2'
import { formatNumber } from 'src/utils/functions'

const pieColors = ['#F68E5D', '#7789EC', '#91D4F6', '#F9D44C', '#F77FB3', '#F75E5E', '#93C572', '#004C6D']

interface LegendItemProps {
  color: string
  name: string
  percentage: number
  isActive: boolean
  onMouseEnter: () => void
  onMouseLeave: () => void
  onClick: () => void
}

const LegendItem = ({ color, name, percentage, isActive, onMouseEnter, onMouseLeave, onClick }: LegendItemProps) => (
  <Box
    display="flex"
    alignItems="center"
    gap="8px"
    py="2px"
    px="4px"
    borderRadius="4px"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      bgcolor: isActive ? `${color}20` : 'transparent',
      transition: 'background-color 0.15s ease'
    }}>
    <Box
      bgcolor={color}
      width="10px"
      height="10px"
      borderRadius="30%"
      flexShrink={0}
    />
    <Typography
      fontSize="11px"
      color="#000"
      lineHeight="initial"
      fontWeight={isActive ? 600 : 400}
      sx={{
        maxWidth: '150px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
      {name}
    </Typography>
    <Typography
      fontSize="11px"
      color="#000"
      lineHeight="initial"
      flexShrink={0}>
      {formatNumber(percentage, { maxFractionDigits: 1 })}%
    </Typography>
  </Box>
)

// Custom shape renderer that handles both active and inactive states
const createShapeRenderer = (activeIndex: number | null) => (props: any) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, index } = props
  const isActive = activeIndex === index
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + (isActive ? 2 : 0)}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  )
}

interface ErrorBreakdownPieChartProps {
  themesData: DelIntelThemeGroupby[]
  breakdownCompWidth: number
  onClick: (theme: string) => void
}

const ErrorBreakdownPieChart = ({ themesData, breakdownCompWidth, onClick }: ErrorBreakdownPieChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  // Sort data once to maintain consistent ordering - memoized for stable reference
  const sortedThemesData = useMemo(
    () => [...themesData].sort((a, b) => b.error_charge_contribution - a.error_charge_contribution),
    [themesData]
  )

  const handlePieEnter = useCallback((_: any, index: number) => {
    setActiveIndex(index)
  }, [])

  const handlePieLeave = useCallback(() => {
    setActiveIndex(null)
  }, [])

  const handleLegendEnter = (theme: string) => {
    const index = sortedThemesData.findIndex((d) => d.theme === theme)
    setActiveIndex(index !== -1 ? index : null)
  }

  const handleLegendLeave = useCallback(() => {
    setActiveIndex(null)
  }, [])

  // Get active data for tooltip
  const activeData = activeIndex !== null ? sortedThemesData[activeIndex] : null
  const activeColor = activeIndex !== null ? pieColors[activeIndex % pieColors.length] : null

  return (
    <PieChart
      pies={{
        data: sortedThemesData,
        dataKey: 'error_charge_contribution',
        outerRadius: 100,
        innerRadius: 45,
        onClick: (props) => onClick(get(props, 'theme', '') as string),
        onMouseEnter: handlePieEnter,
        onMouseLeave: handlePieLeave,
        shape: createShapeRenderer(activeIndex),
        cursor: 'pointer',
        minAngle: 1.5,
        cornerRadius: 1,
        strokeWidth: 1
      }}
      cells={sortedThemesData.map((_, index) => ({
        dataKey: 'error_charge_contribution',
        fill: pieColors[index % pieColors.length]
      }))}
      options={{
        tooltip: true,
        legend: true
      }}
      configs={{
        pieChartConfig: {
          height: 200,
          width: breakdownCompWidth
        },
        tooltipConfig: {
          active: activeIndex !== null,
          content: () => {
            if (activeData === null || activeColor === null) return null
            const percentage = (activeData.error_charge_contribution || 0) * 100
            const name = activeData.theme || ''

            return (
              <Box
                bgcolor="#FFF"
                border="1px solid #E6E6E6"
                borderRadius="4px"
                px="8px"
                py="4px"
                display="flex"
                alignItems="center"
                gap="8px">
                <Box
                  bgcolor={activeColor}
                  width="10px"
                  height="10px"
                  borderRadius="30%"
                />
                <Typography
                  fontSize="11px"
                  color="#000"
                  fontWeight={500}>
                  {name}
                </Typography>
                <Typography
                  fontSize="11px"
                  color="#000">
                  {formatNumber(percentage, { maxFractionDigits: 1 })}%
                </Typography>
              </Box>
            )
          }
        },
        legendConfig: {
          align: 'right',
          verticalAlign: 'middle',
          layout: 'vertical',
          content: ({ payload }) => {
            if (!payload) return null
            const trim = 7
            const items = payload.slice(0, trim)
            const remaining = payload.slice(trim)

            return (
              <Box
                display="flex"
                flexDirection="column"
                gap="2px">
                {items.map((entry, index) => {
                  const theme = get(entry, 'payload.theme', '') as string
                  const themeIndex = sortedThemesData.findIndex((d) => d.theme === theme)
                  return (
                    <LegendItem
                      key={index}
                      color={entry.color as string}
                      name={theme}
                      percentage={(get(entry, 'payload.error_charge_contribution', 0) as number) * 100}
                      isActive={activeIndex === themeIndex}
                      onMouseEnter={() => handleLegendEnter(theme)}
                      onMouseLeave={handleLegendLeave}
                      onClick={() => onClick(theme)}
                    />
                  )
                })}
                {remaining.length === 1 && (() => {
                  const theme = get(remaining[0], 'payload.theme', '') as string
                  const themeIndex = sortedThemesData.findIndex((d) => d.theme === theme)
                  return (
                    <LegendItem
                      color={remaining[0].color as string}
                      name={theme}
                      percentage={(get(remaining[0], 'payload.error_charge_contribution', 0) as number) * 100}
                      isActive={activeIndex === themeIndex}
                      onMouseEnter={() => handleLegendEnter(theme)}
                      onMouseLeave={handleLegendLeave}
                      onClick={() => onClick(theme)}
                    />
                  )
                })()}
                {remaining.length > 1 && (
                  <LegendItem
                    color="#999"
                    name={`+${remaining.length} more`}
                    percentage={remaining.reduce((acc, curr) => acc + (get(curr, 'payload.error_charge_contribution', 0) as number), 0) * 100}
                    isActive={false}
                    onMouseEnter={() => {}}
                    onMouseLeave={() => {}}
                    onClick={() => onClick(get(remaining[0], 'payload.theme', '') as string)}
                  />
                )}
              </Box>
            )
          }
        }
      }}
    />
  )
}

export default ErrorBreakdownPieChart
